/**
 * Positional evaluation, derived from a spec rather than written for a game.
 *
 * Everything here is computed from `rules`: the winning windows come from
 * `terminal.win` (each condition's length and directions, walked over the
 * board), and the centre term comes from `board.cols`. There is not one
 * Connect 4 constant in this file, so an authored 9x7 "Drop Five" is evaluated
 * by the same code — which is the whole point of a spec-driven engine.
 *
 * Boundary contract, matching `spec/interpreter.ts`:
 *  - pure: no I/O, no clock, no randomness;
 *  - inputs are treated as frozen and never mutated;
 *  - total: nonsense in-type specs produce nonsense scores, never a throw and
 *    never a hang. Every loop is bounded by the board's own size.
 */
import { boardSize, colOf, indexOf } from '../spec/interpreter.ts';
import type { BoardOf, SeatOf } from '../spec/interpreter.ts';
import type { RulesSpec } from '../spec/schema.ts';

/**
 * Windows outweigh the centre term by this much.
 *
 * The centre is a heuristic about *future* windows; an actual near-complete
 * window is evidence about this position. Keeping the scales an order apart
 * stops "sat in the middle" from ever outranking "three in a row".
 */
export const WINDOW_SCALE = 4;

/** One occupied cell at the most central column is worth this per unit of centrality. */
export const CENTER_SCALE = 1;

/**
 * The heuristic is clamped to this magnitude so it can never be mistaken for a
 * proven result. `search.ts` reserves everything above it for mate distances;
 * without the clamp, a 32x32 board with four win conditions could sum past the
 * mate band and invent a forced win that does not exist.
 */
export const HEURISTIC_MAX = 100_000;

/**
 * What `count` of a seat's own pieces inside an otherwise empty window is worth.
 *
 * Cubic, so progress towards a line accelerates: for a length-4 window the
 * steps are 1, 8, 27 — a third piece is worth more than three separate first
 * pieces, which is the shape of the actual tactic. A full window is only ever
 * produced by a terminal position, which the search scores as a mate instead.
 */
export function windowWeight(count: number): number {
  return count * count * count;
}

/**
 * A window that a seat could still complete: `length` board indices in a
 * straight line, from one of the spec's win conditions.
 */
export type WinWindow = readonly number[];

/**
 * Every winning window on the board, deduplicated.
 *
 * Two conditions (or a direction and its negation, which the schema rejects
 * within one condition but cannot prevent across two) can describe the same set
 * of cells; counting it twice would silently double that tactic's value.
 */
export function enumerateWinWindows(rules: RulesSpec): readonly WinWindow[] {
  const { cols, rows } = rules.board;
  const windows: WinWindow[] = [];
  const seen = new Set<string>();

  for (const condition of rules.terminal.win) {
    const length = condition.length;
    for (const [dRow, dCol] of condition.directions) {
      if (dRow === 0 && dCol === 0) continue;
      for (let row = 0; row < rows; row += 1) {
        for (let col = 0; col < cols; col += 1) {
          const endRow = row + (length - 1) * dRow;
          const endCol = col + (length - 1) * dCol;
          if (endRow < 0 || endRow >= rows || endCol < 0 || endCol >= cols) continue;

          const cells: number[] = [];
          for (let step = 0; step < length; step += 1) {
            cells.push(indexOf(rules, row + step * dRow, col + step * dCol));
          }
          const key = [...cells].sort((a, b) => a - b).join(',');
          if (seen.has(key)) continue;
          seen.add(key);
          windows.push(Object.freeze(cells));
        }
      }
    }
  }

  return Object.freeze(windows);
}

/**
 * The longest line the spec asks anyone to build.
 *
 * `review.ts` scales its verdict thresholds off this, so "a serious mistake"
 * means the same thing on a board that needs five in a row as on one that
 * needs four.
 */
export function longestWinLength(rules: RulesSpec): number {
  let longest = 0;
  for (const condition of rules.terminal.win) {
    if (condition.length > longest) longest = condition.length;
  }
  return longest;
}

/**
 * How central each cell's column is: the middle column scores highest, the
 * edges zero. `(cols - 1) / 2` handles even and odd widths without a special
 * case.
 *
 * Shared with `position.ts` rather than restated there, so the incremental
 * evaluator and the from-scratch one cannot drift apart by a rounding rule.
 */
export function centralityTable(rules: RulesSpec): Int32Array {
  const size = boardSize(rules);
  const middle = (rules.board.cols - 1) / 2;
  const table = new Int32Array(size);
  for (let index = 0; index < size; index += 1) {
    table[index] = Math.round((middle - Math.abs(colOf(rules, index) - middle)) * CENTER_SCALE);
  }
  return table;
}

/** Clamp a raw sum into the heuristic band. */
export function clampHeuristic(score: number): number {
  if (score > HEURISTIC_MAX) return HEURISTIC_MAX;
  if (score < -HEURISTIC_MAX) return -HEURISTIC_MAX;
  return score;
}

export interface Evaluator<S extends RulesSpec> {
  /** Every winning window, in enumeration order. Exposed for tests and tooling. */
  readonly windows: readonly WinWindow[];
  /**
   * A heuristic score for `board` from `seat`'s point of view, in `[-HEURISTIC_MAX,
   * HEURISTIC_MAX]`. Positive is good for `seat`. Every other seat counts as the
   * opposition, which is exactly true for the two-seat games the result
   * vocabulary can score and a sane approximation beyond it.
   */
  evaluate(board: BoardOf<S>, seat: SeatOf<S>): number;
}

/**
 * Precompute the spec-derived tables once, then evaluate many positions.
 *
 * The window list is flattened into typed arrays because the search calls
 * `evaluate` at every leaf; walking an array of arrays there costs more in
 * pointer chasing than the whole rest of the evaluation.
 */
export function createEvaluator<S extends RulesSpec>(rules: S): Evaluator<S> {
  const windows = enumerateWinWindows(rules);
  const size = boardSize(rules);
  const empty = rules.cells.empty;

  let total = 0;
  for (const window of windows) total += window.length;
  const cells = new Int32Array(total);
  const offsets = new Int32Array(windows.length + 1);
  let cursor = 0;
  for (const [index, window] of windows.entries()) {
    offsets[index] = cursor;
    for (const cell of window) {
      cells[cursor] = cell;
      cursor += 1;
    }
  }
  offsets[windows.length] = cursor;

  const longest = longestWinLength(rules);
  const weights = new Int32Array(longest + 1);
  for (let count = 0; count <= longest; count += 1) weights[count] = windowWeight(count);

  const centrality = centralityTable(rules);

  const windowCount = windows.length;

  function evaluate(board: BoardOf<S>, seat: SeatOf<S>): number {
    let score = 0;

    for (let index = 0; index < windowCount; index += 1) {
      const start = offsets[index] ?? 0;
      const end = offsets[index + 1] ?? start;
      let owner = empty as number;
      let count = 0;
      let contested = false;

      for (let cursorIndex = start; cursorIndex < end; cursorIndex += 1) {
        const value = board[cells[cursorIndex] ?? 0] ?? empty;
        if (value === empty) continue;
        if (count === 0) {
          owner = value;
          count = 1;
          continue;
        }
        if (value === owner) {
          count += 1;
          continue;
        }
        contested = true;
        break;
      }

      if (contested || count === 0) continue;
      const weight = (weights[count] ?? 0) * WINDOW_SCALE;
      score += owner === seat ? weight : -weight;
    }

    for (let index = 0; index < size; index += 1) {
      const value = board[index] ?? empty;
      if (value === empty) continue;
      const weight = centrality[index] ?? 0;
      score += value === seat ? weight : -weight;
    }

    return clampHeuristic(score);
  }

  return { windows, evaluate };
}
